import { useState } from 'react'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { Pagination, Table } from 'antd'
import { SearchInput, Loader } from '../../components'
import { getOperatorUsers } from '../../services/userService'

const T_PREFIX = 'cycle_counts.assign_modal'

export const OperatorSelectTable = ({
  // Radio mode props (default, backward compatible)
  selectionType = 'radio',
  selectedOperator,
  onOperatorSelect,
  // Checkbox mode props
  selectedOperators = [],
  onOperatorsChange,
  // Filtering by warehouse + role
  warehouseId,
  role,
  roles,
  // Rows to render greyed-out and unselectable (e.g. the counter of the locations being reviewed)
  disabledOperatorIds = [],
  // pending_cycle_counts only tallies counting work (see the backend association), so it
  // reads as 0 for every reviewer — hide the column rather than show a number that lies.
  showPendingCountsColumn = true,
}) => {
  const { i18n } = useTranslation()
  const isCheckbox = selectionType === 'checkbox'
  const disabledIdSet = new Set((disabledOperatorIds || []).map(id => id.toString()))

  const [searchParams, setSearchParams] = useState({
    search: '',
    page: 1,
    per_page: 10,
  })
  const [selectedKeys, setSelectedKeys] = useState([])

  const queryParams = {
    ...searchParams,
    ...(warehouseId != null ? { warehouse_id: parseInt(warehouseId) } : {}),
    ...(role ? { role } : {}),
    ...(roles ? { roles } : {}),
  }

  const {
    data: operatorsRawData,
    isFetching,
    isLoading,
  } = useQuery(['cycle_count_operators', queryParams], () => getOperatorUsers(queryParams), {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })

  const operatorsTableData = (operatorsRawData?.users || []).map(op => {
    const composedName = `${op.first_name || ''} ${op.last_name || ''}`.trim()
    return {
      key: op.id.toString(),
      ...op,
      full_name: op.full_name || composedName || op.email,
      pending_cycle_counts: op.pending_cycle_counts || 0,
      _disabled: disabledIdSet.has(op.id.toString()),
    }
  })

  const columns = [
    {
      title: i18n.t(`${T_PREFIX}.operator_name`),
      dataIndex: 'full_name',
      key: 'full_name',
      render: (fullName, record) => (
        <div>
          <span>{fullName}</span>
          {record.email && record.email !== fullName && (
            <div className="text-xs text-gray-400 truncate max-w-[200px]" title={record.email}>
              {record.email}
            </div>
          )}
        </div>
      ),
    },
    ...(showPendingCountsColumn
      ? [
          {
            title: i18n.t(`${T_PREFIX}.pending_counts`),
            dataIndex: 'pending_cycle_counts',
            key: 'pending_cycle_counts',
            render: count => <span className={count > 0 ? 'text-yellow-600 font-medium' : 'text-gray-400'}>{count}</span>,
          },
        ]
      : []),
  ]

  // --- Radio mode handlers (unchanged) ---
  const handleSearchRadio = v => {
    setSelectedKeys([])
    onOperatorSelect(null)
    setSearchParams({ ...searchParams, search: v, page: 1 })
  }

  const handleRowClickRadio = record => {
    setSelectedKeys([record.id.toString()])
    onOperatorSelect(record)
  }

  const handlePageChangeRadio = page => {
    setSelectedKeys([])
    onOperatorSelect(null)
    setSearchParams({ ...searchParams, page })
  }

  // --- Checkbox mode handlers ---
  const handleSearchCheckbox = v => {
    setSearchParams({ ...searchParams, search: v, page: 1 })
  }

  const handlePageChangeCheckbox = page => {
    setSearchParams({ ...searchParams, page })
  }

  const handleCheckboxChange = (selectedRowKeys, selectedRows) => {
    const currentPageIds = new Set(operatorsTableData.map(op => op.id.toString()))
    const otherPageSelections = selectedOperators.filter(op => !currentPageIds.has(op.id.toString()))
    const newSelections = [...otherPageSelections, ...selectedRows]
    onOperatorsChange(newSelections)
  }

  const handleRowClickCheckbox = record => {
    const isSelected = selectedOperators.some(op => op.id === record.id)
    if (isSelected) {
      onOperatorsChange(selectedOperators.filter(op => op.id !== record.id))
    } else {
      onOperatorsChange([...selectedOperators, record])
    }
  }

  // --- Derived state ---
  const effectiveKeysRadio = selectedOperator ? [selectedOperator.id.toString()] : selectedKeys.length > 0 && !selectedOperator ? [] : selectedKeys

  const effectiveKeysCheckbox = selectedOperators.map(op => op.id.toString())

  const rowSelection = isCheckbox
    ? {
        type: 'checkbox',
        onChange: handleCheckboxChange,
        selectedRowKeys: effectiveKeysCheckbox,
        preserveSelectedRowKeys: true,
        getCheckboxProps: record => ({ disabled: record._disabled }),
      }
    : {
        type: 'radio',
        onChange: (selectedRowKeys, selectedRows) => {
          setSelectedKeys(selectedRowKeys)
          onOperatorSelect(selectedRows[0])
        },
        selectedRowKeys: effectiveKeysRadio,
        getCheckboxProps: record => ({ disabled: record._disabled }),
      }

  return (
    <>
      <div className="my-2 mx-2">
        <SearchInput
          onChange={isCheckbox ? handleSearchCheckbox : handleSearchRadio}
          debounce={250}
          placeholder={i18n.t(`${T_PREFIX}.search_operator`)}
          className="focus-within:text-gray-600 text-gray-400"
        />
      </div>

      <div className="relative">
        <Loader show={isFetching || isLoading} />
        <Table
          rowSelection={rowSelection}
          size="small"
          columns={columns}
          dataSource={operatorsTableData}
          pagination={false}
          rowClassName={record => (record._disabled ? 'opacity-50 cursor-not-allowed' : '')}
          onRow={record => ({
            onClick: () => {
              if (record._disabled) return
              isCheckbox ? handleRowClickCheckbox(record) : handleRowClickRadio(record)
            },
          })}
        />
        <div className="flex justify-center mt-2 pb-2">
          <Pagination
            size="small"
            total={operatorsRawData?.meta?.total_count || 0}
            showTotal={total => (
              <span>
                {total} {i18n.t(`${T_PREFIX}.operators`)}
              </span>
            )}
            current={searchParams.page}
            onChange={isCheckbox ? handlePageChangeCheckbox : handlePageChangeRadio}
            showSizeChanger={false}
          />
        </div>
      </div>
    </>
  )
}
